/**
 * EditorPrecoCatalogo — a caixinha que corrige o preco de um item DENTRO da cotacao.
 *
 * Existe porque o preco da grade e espelho do Catalogo do Fornecedor (DA-283) e, ate aqui, corrigir
 * um numero errado obrigava a sair da tela, achar o item no Catalogo e voltar. A ferramenta passou a
 * morar onde o problema aparece — mas SEM furar a fonte canonica: o que se digita aqui vai pro
 * catalogo (`PUT /api/fornecedor-catalogo/preco`), nao pra um campo solto da cotacao.
 *
 * POR QUE DOIS CAMPOS, e nao um input no unitario:
 *   O que o fornecedor cota e o preco DA EMBALAGEM (a caixa, a bobina, o pacote). O "Unitario" que a
 *   grade ranqueia e ele dividido pela quantidade de dentro. Em 32% das linhas de resposta a
 *   quantidade NAO e 1 — caixa de 200 parafusos, disjuntor em caixa de 12, bobina de 600m. Nessas,
 *   um input direto no unitario faria o operador dividir de cabeca, e o erro previsivel (digitar o
 *   preco do pacote no campo do unitario, ou o contrario) e exatamente o que ja aconteceu 27 vezes no
 *   banco: pacote gravado com valor de peca, item virando o mais barato do mundo e ganhando a compra
 *   calado. Mostrando os dois campos com o unitario recalculando ao vivo, o erro fica visivel ANTES
 *   de gravar. E quando a quantidade e 1 (o caso do cobre, por quilo), o segundo campo simplesmente
 *   nao atrapalha: o unitario embaixo repete o numero digitado.
 *
 * A mesma peca serve as duas vistas (Por produto e Lado a lado) — gesto igual, campos iguais, mesmo
 * vocabulario do modal do Catalogo. `position: fixed` ancorado no retangulo da celula, porque a
 * grade rola dentro de um container (regra do CLAUDE.md: dropdown em `overflow-y-auto` nao usa
 * `absolute`); vira pra cima quando nao cabe embaixo.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { parseQtd, precoEmbComDifal, difalSeAplica, aliquotaDifalValida, ALIQUOTAS_DIFAL } from '@fiodecobre/shared';

// 288 cabia so preco + qtd. Desde 28/10/2026 a linha leva tambem o FORNECEDOR, so-leitura (pedido
// dele: "ajuda a nao alterar o preco do fornecedor errado"), entao a caixa cresce pra ele caber
// sem empurrar os dois campos editaveis.
const LARGURA = 460;
/** Altura estimada da caixa — so decide se abre pra cima ou pra baixo. */
const ALTURA = 232;

/**
 * pt-BR com ate 5 casas (a coluna e `numeric(13,5)` desde o DA-317) e no minimo 2, pra nao
 * transformar "99,90" em "99,9" na ida e volta.
 */
function fmtPreco(v: number): string {
  if (!Number.isFinite(v) || v <= 0) return '';
  return v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 5 });
}

export interface AlvoEdicaoPreco {
  itemId: number;
  fornId: number;
  respIdx: number;
  /** retangulo da celula clicada — a caixa nasce colada nela */
  rect: { top: number; left: number; bottom: number; right: number; width: number };
  fornNome: string;
  codigo: string;
  descricao: string;
  embalagem: string;
  precoEmb: string;
  qtdEmb: string;
  /** UF da perna e aliquota do Monitor ([DA-727]) — so pra mostrar o preco com DIFAL ao vivo. */
  filialUf: string | null;
  difalPct: number | null;
}

export function EditorPrecoCatalogo(props: {
  alvo: AlvoEdicaoPreco;
  salvando: boolean;
  onCancelar: () => void;
  /** `difalPct` so vem quando a aliquota MUDOU — sem a chave a rota nao mexe nela. */
  onConfirmar: (v: { precoEmb: number; qtdEmb: number; difalPct?: number | null }) => void;
}) {
  const { alvo, salvando, onCancelar, onConfirmar } = props;

  const [preco, setPreco] = useState(alvo.precoEmb || '');
  const [qtd, setQtd] = useState(alvo.qtdEmb || '1');
  // Aliquota do DIFAL ([DA-860]): abre com a que esta gravada, ou VAZIA ("%", a conferir) — nunca
  // pre-escolhida, porque escolher e' a decisao que o operador esta tomando aqui.
  const difalInicial = aliquotaDifalValida(alvo.difalPct) ? String(Number(alvo.difalPct)) : '';
  const [aliq, setAliq] = useState(difalInicial);
  const caixaRef = useRef<HTMLDivElement | null>(null);
  const precoRef = useRef<HTMLInputElement | null>(null);

  // Foco sincrono (layout effect, nao setTimeout): quem ja veio digitando nao pode perder os
  // primeiros caracteres — mesma regra do ComboBox no DA-305.
  useLayoutEffect(() => {
    precoRef.current?.focus();
    precoRef.current?.select();
  }, []);

  // Clicar fora fecha sem gravar. Nao e modal (que so fecha no X/Cancelar/Esc) — e um popover de
  // celula, e o gesto esperado dele e o dos menus ⋮ da propria tela.
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (caixaRef.current && !caixaRef.current.contains(e.target as Node)) onCancelar();
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onCancelar]);

  const precoN = parseQtd(preco);
  const qtdN = parseQtd(qtd);
  const precoOk = Number.isFinite(precoN) && precoN > 0;
  const qtdOk = Number.isFinite(qtdN) && qtdN > 0;
  const unitario = precoOk && qtdOk ? precoN / qtdN : 0;
  // O que se digita e' o preco do FORNECEDOR; com DIFAL e' o que a celula vai mostrar depois de
  // gravar (pedido dele, 28/10/2026: "eu digito 200,00 e o sistema mostra 208,98").
  const temDifal = difalSeAplica(alvo.filialUf);
  const aliqN = aliq === '' ? null : Number(aliq);
  const difalEstimado = !aliquotaDifalValida(aliqN);
  const comDifal = temDifal && precoOk ? precoEmbComDifal(precoN, alvo.filialUf, aliqN) : 0;
  const podeSalvar = precoOk && qtdOk && !salvando;

  function confirmar() {
    if (!podeSalvar) return;
    onConfirmar({
      precoEmb: precoN,
      qtdEmb: qtdN,
      ...(temDifal && aliq !== difalInicial ? { difalPct: aliqN } : {}),
    });
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onCancelar(); return; }
    // Enter grava de qualquer um dos dois campos. O `enter-as-tab` global levaria o foco adiante e
    // aqui isso so faria o operador sair da caixa sem gravar.
    if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); confirmar(); }
  }

  // Cabe embaixo? Senao vira pra cima. E nunca vaza pela direita da janela.
  const abreParaCima = alvo.rect.bottom + ALTURA > window.innerHeight;
  const top = abreParaCima ? Math.max(8, alvo.rect.top - ALTURA - 4) : alvo.rect.bottom + 4;
  const left = Math.max(8, Math.min(alvo.rect.right - LARGURA, window.innerWidth - LARGURA - 8));

  // Rotulo de UMA linha sempre. Se quebrasse, o campo de baixo descia junto e os dois inputs saiam
  // do mesmo eixo — sigla comprida (PCT, FRD) e cortada com reticencias em vez de empurrar o campo.
  const rotulo: React.CSSProperties = {
    fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
    color: 'var(--text-muted)', letterSpacing: 0.3, marginBottom: 2, display: 'block',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  };
  const campo: React.CSSProperties = {
    width: '100%', padding: '5px 8px', borderRadius: 4,
    fontSize: 13, fontWeight: 700, textAlign: 'right',
  };

  return (
    <div
      ref={caixaRef}
      onKeyDown={onKeyDown}
      // A vista lado a lado e um painel `fixed` em z-index 25; a caixa precisa passar por cima dela
      // (e ficar abaixo do PainelEscolha, que e modal em 1200).
      style={{
        position: 'fixed', top, left, width: LARGURA, zIndex: 1150,
        background: 'var(--panel-bg)', border: '1px solid var(--primary)', borderRadius: 8,
        boxShadow: '0 12px 32px rgba(0,0,0,0.22)', padding: 10,
        display: 'flex', flexDirection: 'column', gap: 8,
      }}
    >
      <div style={{ borderBottom: '1px solid var(--border)', paddingBottom: 6 }}>
        {/* A sigla da embalagem mora AQUI, como selo — do lado do codigo, igual a grade faz. No
            rotulo do campo ela nao cabia: "QTD NA EMB. (UN)" estourava a coluna e saia cortado. */}
        <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: 5 }}>
          <span>{alvo.fornNome}{alvo.codigo ? ` · ${alvo.codigo}` : ''}</span>
          {alvo.embalagem && (
            <span style={{ background: 'var(--border)', color: 'var(--input-fg)', borderRadius: 3, padding: '0 4px', fontSize: 9.5, fontWeight: 700 }}>
              {alvo.embalagem}
            </span>
          )}
        </div>
        <div
          title={alvo.descricao}
          style={{ fontSize: 10.5, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {alvo.descricao || 'Sem descrição do fornecedor'}
        </div>
      </div>

      {/* `flex-end` e a rede: mesmo que um rotulo cresca, os dois inputs continuam no mesmo eixo. */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        {/* FORNECEDOR so-leitura: mesma moldura dos campos, sem foco nem Tab (nao e' input), fundo
            apagado pra nao parecer editavel. Quem grava e' a linha clicada; isto so confirma qual. */}
        <div style={{ flex: '1.3 1 0', minWidth: 0 }}>
          <label style={rotulo}>Fornecedor</label>
          <div
            title={alvo.fornNome}
            style={{
              // Sem borda: os inputs vizinhos nao tem (medido 0px), e 1px aqui deixava o campo 1,6px
              // mais alto. Cor do texto da pagina: o `--text-muted` sobre este fundo deu 3,47:1.
              ...campo, textAlign: 'left',
              background: 'var(--grid-row-alt-bg, rgba(0,0,0,0.04))', color: 'var(--page-fg)',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}
          >
            {alvo.fornNome}
          </div>
        </div>
        <div style={{ flex: '1 1 0', minWidth: 0 }}>
          <label style={rotulo}>{temDifal ? 'Preço emb. s/DIFAL' : 'Preço da emb. (R$)'}</label>
          <input
            ref={precoRef}
            type="text"
            inputMode="decimal"
            value={preco}
            onChange={(e) => setPreco(e.target.value)}
            onFocus={(e) => e.target.select()}
            style={campo}
          />
        </div>
        <div style={{ flex: '0 0 96px', minWidth: 0 }}>
          <label style={rotulo}>Qtd na emb.</label>
          <input
            type="text"
            inputMode="decimal"
            value={qtd}
            onChange={(e) => setQtd(e.target.value)}
            onFocus={(e) => e.target.select()}
            style={campo}
          />
        </div>
      </div>

      {/* O numero que a grade compara. Fica a vista enquanto se digita justamente pra denunciar a
          troca de campo antes de gravar (pacote de 200 a R$ 0,29 aparece aqui como 0,0015). */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        background: 'var(--grid-row-alt-bg, rgba(0,0,0,0.04))', borderRadius: 4, padding: '4px 8px',
      }}>
        <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>
          Unitário
        </span>
        <strong style={{ fontSize: 14, color: unitario > 0 ? 'var(--primary)' : 'var(--text-muted)' }}>
          {unitario > 0 ? fmtPreco(unitario) : '—'}
        </strong>
      </div>

      {/* Oferta de fora de GO: escolhe a aliquota aqui mesmo ([DA-860]) — mesma caixa do Monitor,
          laranja enquanto vazia. Vazia a conta segue estimando 4,49%, e o valor ao lado diz isso. */}
      {temDifal && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '0 8px' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>
              DIFAL
            </span>
            <select
              value={aliq}
              onChange={(e) => setAliq(e.target.value)}
              title={aliq === ''
                ? 'Alíquota do DIFAL ainda não conferida — a conta usa 4,49% até você escolher. 4,49% = nacional; 7,87% = importado.'
                : 'Alíquota do DIFAL desta oferta. 4,49% = nacional; 7,87% = importado.'}
              style={{
                fontSize: 12, padding: '2px 4px', cursor: 'pointer', fontWeight: 600,
                ...(aliq === ''
                  ? { '--input-bg': '#f97316', '--input-fg': '#000', '--input-border': '#c2410c' }
                  : {}),
              } as React.CSSProperties}
            >
              <option value="">% a conferir</option>
              {ALIQUOTAS_DIFAL.map((a) => (
                <option key={a} value={String(a)}>{a.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}%</option>
              ))}
            </select>
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>
              Com DIFAL{difalEstimado ? ' (est. 4,49%)' : ''}
            </span>
            <strong style={{ fontSize: 13 }}>
              {comDifal > 0 ? comDifal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
            </strong>
          </span>
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onCancelar}
          disabled={salvando}
          style={{
            padding: '5px 12px', borderRadius: 4, border: '1px solid var(--border)',
            background: 'transparent', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer',
          }}
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={confirmar}
          disabled={!podeSalvar}
          className="btn-primario"
          style={{ padding: '5px 14px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 5, opacity: podeSalvar ? 1 : 0.5 }}
        >
          {salvando && <Loader2 size={12} className="animate-spin" />}
          Salvar
        </button>
      </div>
    </div>
  );
}
